import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, firstValueFrom } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// Services
import { ApiService } from 'src/app/services/api.service';
import { RefreshService } from 'src/app/refresh.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';

// Constants
import * as vo from 'src/app/core/model/server/vo/_index';
import { TRANSACTION_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { TRANSFER_FUNDS_TABLE_SUB_HEADERS } from 'src/app/lib/constants/transactions.constants';

// Shared components / modules
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { SharedPipeModule } from 'src/app/shared/modules/shared-pipe.module';
import { DateInputComponent } from 'src/app/shared/component/date-input/date-input.component';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { PaginationComponent } from 'src/app/shared/component/pagination/pagination.component';
import { TableControlsComponent } from 'src/app/shared/component/table-controls/table-controls.component';
import { DropdownFieldV2Component } from 'src/app/shared/component/custom-dropdown-v2/custom-dropdown-v2.component'; // adjust path

@Component({
  selector: 'app-transfer-funds',
  templateUrl: './transfer-funds.component.html',
  styleUrls: ['./transfer-funds.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltip,
    NgxSpinnerModule,
    SharedComponentsModule,
    SharedPipeModule,
    DateInputComponent,
    MegapayLoaderComponent,
    PaginationComponent,
    TableControlsComponent,
    DropdownFieldV2Component,
  ],
})
export class TransferFundsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  tabs = TRANSACTION_TAB_OPTIONS;

  form!: UntypedFormGroup;
  searchForm!: UntypedFormGroup;
  limitForm!: UntypedFormGroup;

  // ─── Dropdown ──────────────────────────────────────────────────────────────
  transactionTypeOptions = [
    { label: 'All',           value: null },
    { label: 'Scan QR',       value: '2' },
    { label: 'Transfer Fund', value: '4' },
  ];
  transactionType: string | null = null;
  transactionType_text = '';

  // ─── Table ─────────────────────────────────────────────────────────────────
  tableSubHeader = TRANSFER_FUNDS_TABLE_SUB_HEADERS;
  itemLists: vo.ITransferFundsTransaction[] = [];
  submitted = false;
  isLoading = false;

  /** Submitted filter criteria (the form itself is cleared after submit) */
  filterFields: vo.TransferFundsFilterFields | null = null;

  // ─── Pagination ────────────────────────────────────────────────────────────
  size = 10;
  total_pages = 0;
  current_page = 1;
  total = 0;

  // ─── Search & Limit ────────────────────────────────────────────────────────
  search_keyword = '';
  searchKey: any = null;
  limitKey: any = null;

  // ─── Sorting ───────────────────────────────────────────────────────────────
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  constructor(
    private fb: UntypedFormBuilder,
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private refreshService: RefreshService,
  ) {
    this.initializeForm();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetTableState();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch((err) => console.error('Spinner hide error on destroy:', err));
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      transactionId:       [null],
      senderNumber:        [null],
      recipientNumber:     [null],
      dateFrom:            [null],
      dateTo:              [null],
      senderFirstName:     [null],
      senderMiddleName:    [null],
      senderLastName:      [null],
      recipientFirstName:  [null],
      recipientMiddleName: [null],
      recipientLastName:   [null],
    });

    this.searchForm = this.fb.group({ search: [''] });
    this.limitForm  = this.fb.group({ limit: [this.size] });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page   = 1;
      this.search_keyword = this.searchForm.value.search;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size         = this.limitForm.value.limit;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);
  }

  // ─── Request Payload ───────────────────────────────────────────────────────

  private buildFilterFields(): vo.TransferFundsFilterFields {
    const v = this.form.value;
    return {
      transactionId:       v.transactionId,
      senderNumber:        v.senderNumber,
      recipientNumber:     v.recipientNumber,
      dateFrom:            v.dateFrom,
      dateTo:              v.dateTo,
      senderFirstName:     v.senderFirstName,
      senderMiddleName:    v.senderMiddleName,
      senderLastName:      v.senderLastName,
      recipientFirstName:  v.recipientFirstName,
      recipientMiddleName: v.recipientMiddleName,
      recipientLastName:   v.recipientLastName,
      transactionType:     this.transactionType,
    };
  }

  /** Filters + current search + current sort. Used by both the list call and the export. */
  private buildParams(): vo.TransferFundsTransactionsRequest {
    return {
      ...this.filterFields,
      search: this.search_keyword,
      sortBy: { [this.sort_column]: this.sort_type },
    };
  }

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  submit(): void {
    if (!this.validateDates()) {
      return;
    }

    this.current_page = 1;
    this.filterFields = this.buildFilterFields();
    this.submitted    = true;

    this.get_filtered_list();

    // Clear the inputs; the submitted criteria live on in filterFields
    this.form.reset();
    this.resetDropdowns();
  }

  get_filtered_list(): void {
    this.isLoading = true;
    this.spinner.show().catch((err) => console.error('Spinner show error:', err));

    this.apiService
      .getTransferFundsTransactions(this.buildParams(), this.current_page, this.size)
      .pipe(
        takeUntil(this.destroy$),
        // finalize runs on success, error AND unsubscribe, so the spinner can't get stuck
        finalize(() => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => console.error('Spinner hide error:', err));
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            const result = response.result;
            this.itemLists    = result.data ?? [];
            this.total_pages  = Number(result.totalPages) || 0;
            this.current_page = result.currentPage;
            this.total        = Number(result.totalItems) || 0;
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error}`,
            'Failed Fetching Transfer Funds Transactions',
          );
        },
      });
  }

  // ─── Dropdown Setter ───────────────────────────────────────────────────────

  setTransactionType(text: string, value: string | null): void {
    this.transactionType      = value;
    this.transactionType_text = text;
  }

  private resetDropdowns(): void {
    this.transactionType      = null;
    this.transactionType_text = '';
  }

  // ─── Table Controls Events ─────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.searchList();
    }
  }

  searchList(): void {
    this.current_page   = 1;
    this.search_keyword = this.searchForm.value.search;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  validateDates(): boolean {
    const dateFrom = this.form.get('dateFrom')?.value;
    const dateTo   = this.form.get('dateTo')?.value;

    if (dateFrom && dateTo) {
      const fromDate = new Date(dateFrom);
      const toDate   = new Date(dateTo);

      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(0, 0, 0, 0);

      if (toDate < fromDate) {
        this.toastr.error('Date To cannot be earlier than Date From', 'Invalid Date Range');
        return false;
      }
    }
    return true;
  }

  // ─── Sorting & Pagination ──────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type,
    );

    this.sort_column = column;
    this.sort_type   = type;

    if (this.submitted && this.filterFields) { this.get_filtered_list(); }
  }

  setPage(num: number): void {
    this.current_page = num;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  async print(): Promise<void> {
    if (!this.filterFields) {
      this.toastr.warning('Submit a search first before exporting.', 'Nothing to export');
      return;
    }

    this.spinner.show().catch((err) => console.error(err));
    const fileName = `${new Date().toLocaleDateString()}-transfer-funds.xlsx`;

    try {
      const report = await firstValueFrom(
        this.apiService.exportTransferFundsTransactions(this.buildParams()),
      );
      const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = fileName;
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      window.URL.revokeObjectURL(url);
    } catch (error) {
      this.toastr.error(
        'Please refresh or contact the developer if the error still occurs',
        'Something went wrong',
      );
    } finally {
      this.spinner.hide().catch((err) => console.error(err));
    }
  }

  // ─── Reset ─────────────────────────────────────────────────────────────────

  private resetTableState(): void {
    this.form.reset();
    this.searchForm.reset({ search: '' });
    this.limitForm.reset({ limit: 10 });
    this.resetDropdowns();

    this.itemLists      = [];
    this.submitted      = false;
    this.isLoading      = false;
    this.filterFields   = null;
    this.size           = 10;
    this.total_pages    = 0;
    this.current_page   = 1;
    this.total          = 0;
    this.search_keyword = '';
    this.sort_column    = 'dateTime';
    this.sort_type      = 'ASC';
  }
}
